import { AnimatePresence, motion } from "motion/react";
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import Icon from "@/src/lib/components/custom/Icon";
import { Image } from "@/src/lib/components/custom/Image";
import { Skeleton } from "@/src/lib/components/ui/skeleton";
import { cn } from "@/src/lib/utils";
import { buttonVariants } from "../../../lib/components/ui/button";
import { Card } from "../../../lib/components/ui/card";
import { SearchInput } from "../../../lib/components/ui/search-input";
import { useApi } from "../../../lib/hooks/api/use-api";
import { useCategoryStore } from "../../../lib/hooks/use-store";
import ItemCard from "./ItemCard";

// Animation variants
const fadeInUp = {
	initial: { opacity: 0, y: 20 },
	animate: { opacity: 1, y: 0 },
	exit: { opacity: 0, y: -20 },
	transition: { duration: 0.3, ease: "easeOut" },
};

const staggerContainer = {
	animate: {
		transition: {
			staggerChildren: 0.05,
		},
	},
};

export default function CategorySection() {
	const { t } = useTranslation();
	const api = useApi();
	const {
		data: categories,
		isLoading: categoriesLoading,
		error: categoriesError,
	} = api.menu.getCategories();
	const { selectedCategory, setSelectedCategory } = useCategoryStore();

	const [submittedSearchQuery, setSubmittedSearchQuery] = useState("");
	const [isSearchMode, setIsSearchMode] = useState(false);
	const resultsRef = useRef<HTMLDivElement | null>(null);

	// Search API call - only when submitted
	const {
		data: searchData,
		isLoading: searchLoading,
		error: searchError,
	} = api.menu.searchItems({ q: submittedSearchQuery, limit: 9 });

	const itemsPerPage = 9;

	const {
		data: itemsData,
		isLoading: itemsLoading,
		error: itemsError,
		fetchNextPage,
		hasNextPage,
		isFetchingNextPage,
	} = api.menu.getItemsInfinite({
		category:
			isSearchMode && submittedSearchQuery
				? undefined
				: selectedCategory || undefined,
		limit: itemsPerPage,
	});

	useEffect(() => {
		if (!submittedSearchQuery.trim() || !isSearchMode) return;
		if (searchLoading) return;

		window.requestAnimationFrame(() => {
			const target = resultsRef.current;
			if (!target) return;

			const y = target.getBoundingClientRect().top + window.scrollY - 80;
			window.scrollTo({ top: y, behavior: "smooth" });
		});
	}, [isSearchMode, submittedSearchQuery, searchLoading]);

	// Flatten all loaded pages for infinite scroll or use search results
	const allItems = useMemo(() => {
		if (isSearchMode && searchData) {
			return searchData.items || [];
		}
		if (!itemsData?.pages) return [];
		return itemsData.pages.flatMap((page) => page.items || []);
	}, [itemsData?.pages, searchData, isSearchMode]);

	const loadMore = () => {
		if (hasNextPage && !isFetchingNextPage) {
			fetchNextPage();
		}
	};

	const categoriesOnlyLoading = categoriesLoading;
	const itemsLoadingState = itemsLoading || searchLoading;

	if (categoriesError || itemsError || searchError) {
		return (
			<section className="mt-12 md:mt-16">
				<div className="text-center text-destructive">
					{t("home.browse.error")}
				</div>
			</section>
		);
	}

	return (
		<section className="md:mt-16">
			<div className="flex flex-col items-center justify-between">
				<div className="flex justify-between items-center gap-2 w-full">
					<motion.h1
						initial={{ opacity: 0, y: 10 }}
						animate={{ opacity: 1, y: 0 }}
						transition={{ duration: 0.4, delay: 0.3, ease: "easeInOut" }}
					>
						{t("home.browse.title")}
					</motion.h1>

					<motion.button
						className={cn(
							buttonVariants({
								variant:
									selectedCategory === null && !isSearchMode
										? "ghost"
										: "primary",
							}),
						)}
						onClick={() => {
							setSelectedCategory(null);
							setIsSearchMode(false);
							setSubmittedSearchQuery("");
						}}
						whileHover={{ y: -2 }}
						whileTap={{ y: 1 }}
						initial={{ opacity: 0, y: 10 }}
						animate={{ opacity: 1, y: 0 }}
						transition={{ duration: 0.4, delay: 0.2 }}
					>
						<span>{t("home.browse.view_all")}</span>
					</motion.button>
				</div>

				<motion.div
					className="flex items-center gap-3 my-4 w-full"
					initial={{ opacity: 0, y: 10 }}
					animate={{ opacity: 1, y: 0 }}
					transition={{ delay: 0.1, duration: 0.4 }}
				>
					<SearchInput
						placeholder={t("home.browse.search_placeholder")}
						debounceMs={0}
						onSubmit={(query) => {
							setSubmittedSearchQuery(query);
							setIsSearchMode(true);
							setSelectedCategory(null);
						}}
						className="text-sm"
					/>
				</motion.div>
			</div>

			{/* Categories Section */}
			<div className="mb-12">
				<motion.div
					className="flex gap-4 overflow-x-auto py-4"
					variants={staggerContainer}
					initial="initial"
					animate="animate"
				>
					{categoriesOnlyLoading
						? Array.from({ length: 8 }).map((_, i) => (
								<div
									key={`category-skeleton-${i}`}
									className="bg-card border shadow-sm flex items-center justify-center p-6 rounded-2xl shrink-0"
									style={{ minWidth: "140px", maxWidth: "160px" }}
								>
									<div className="flex flex-col items-center text-center gap-4">
										<Skeleton className="size-24 rounded-full" />
										<div className="flex flex-col items-center gap-1">
											<Skeleton className="h-4 w-20" />
										</div>
									</div>
								</div>
							))
						: categories?.map((category) => (
								<motion.div
									key={category.slug}
									// Replicating Card styles + interactive styles
									className={cn(
										"bg-card text-card-foreground border shadow-sm", // Card base styles
										"flex items-center justify-center p-6 rounded-2xl cursor-pointer transition-shadow hover:shadow-md",
										selectedCategory === category.name
											? "bg-primary/20 border-primary/40 shadow-md"
											: "bg-card hover:bg-accent",
									)}
									style={{ minWidth: "140px", maxWidth: "160px" }}
									onClick={() => setSelectedCategory(category.name)}
									variants={{
										initial: { opacity: 0, y: 20 },
										animate: { opacity: 1, y: 0 },
										hover: {
											y: -4,
											transition: { duration: 0.2, ease: "easeInOut" },
										},
										tap: { y: 2, transition: { duration: 0.1 } },
									}}
									whileHover="hover"
									whileTap="tap"
									initial="initial"
									animate="animate"
								>
									<div className="flex flex-col items-center text-center gap-4">
										<div
											className={cn(
												"size-24 rounded-full bg-background border overflow-hidden",
												selectedCategory === category.name &&
													"border-primary/40",
											)}
										>
											<Image
												src={
													category.imageUrl ||
													"/static/categories/American_Style_Pizza/icon.webp"
												}
												alt={category.name}
												className="w-full h-full object-cover"
											/>
										</div>
										<div className="flex flex-col items-center gap-1">
											<h4
												className={cn(
													"text-sm font-medium",
													selectedCategory === category.name && "text-primary",
												)}
											>
												{category.name}
											</h4>
										</div>
									</div>
								</motion.div>
							))}
				</motion.div>
			</div>

			{/* Items Section Header */}
			<motion.div
				className="flex items-center justify-between px-1 md:px-2"
				initial={{ opacity: 0 }}
				animate={{ opacity: 1 }}
				transition={{ delay: 0.2 }}
			>
				<div>
					<h2 className="text-2xl font-semibold">
						{isSearchMode && submittedSearchQuery
							? t("home.browse.results_for", { query: submittedSearchQuery })
							: selectedCategory
								? t("home.browse.category_items", {
										category: selectedCategory,
									})
								: t("home.browse.all_items")}
					</h2>
					<div className="text-sm text-muted-foreground mt-2">
						{t("home.browse.items_loaded", { count: allItems.length })}
					</div>
				</div>
			</motion.div>

			{/* Items Grid */}
			<motion.div
				ref={resultsRef}
				className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 gap-6 md:gap-10 md:gap-y-24 mt-8 md:mt-16"
				variants={staggerContainer}
				initial="initial"
				whileInView="animate"
				viewport={{ once: true, amount: 0.01 }}
				// Re-animate when category changes
				key={selectedCategory || (isSearchMode ? "search" : "all")}
			>
				{itemsLoadingState ? (
					Array.from({ length: 9 }).map((_, i) => (
						<Card
							key={`item-skeleton-${i}`}
							className="relative overflow-visible rounded-2xl p-6 bg-card flex flex-col h-full"
						>
							<div className="flex-1 flex flex-col gap-4 mb-4">
								<div className="absolute -top-18 right-10 justify-center pointer-events-none hidden md:flex">
									<div className="size-36 md:size-40 rounded-full bg-background shadow-md flex items-center justify-center">
										<Skeleton className="size-32 md:size-36 rounded-full" />
									</div>
								</div>
								<div className="flex flex-col gap-2">
									<div className="flex items-center gap-4">
										<Skeleton className="size-20 rounded-full md:hidden" />
										<div className="flex-1">
											<Skeleton className="h-6 w-3/4 mb-2" />
											<div className="flex items-center gap-2">
												<Skeleton className="h-4 w-20" />
												<Skeleton className="h-4 w-10" />
											</div>
										</div>
									</div>
									<Skeleton className="h-4 w-full mt-4 hidden md:block" />
									<Skeleton className="h-4 w-2/3 hidden md:block" />
									<div className="flex items-center gap-2 mt-2">
										<Skeleton className="h-6 w-20 rounded-full" />
										<Skeleton className="h-6 w-24 rounded-full" />
									</div>
								</div>
							</div>
							<div className="flex flex-col gap-4">
								<div className="flex items-center justify-between">
									<Skeleton className="h-8 w-16" />
									<Skeleton className="h-10 w-28 rounded-full" />
								</div>
							</div>
						</Card>
					))
				) : allItems.length > 0 ? (
					<>
						<AnimatePresence mode="popLayout" initial={false}>
							{allItems.map((item) => (
								<motion.div
									key={item.id}
									variants={fadeInUp}
									initial="initial"
									whileInView="animate"
									viewport={{ once: true, amount: 0.1 }}
									whileHover={{ y: -5, transition: { duration: 0.2 } }}
									whileTap={{ y: 0 }}
									layout
								>
									<ItemCard item={item} />
								</motion.div>
							))}
						</AnimatePresence>

						{hasNextPage && !isSearchMode && (
							<motion.div
								className="col-span-full md:-mt-12 flex justify-center"
								initial={{ opacity: 0, y: 10 }}
								animate={{ opacity: 1, y: 0 }}
								transition={{ delay: 0.2 }}
							>
								<motion.button
									onClick={loadMore}
									className={cn(buttonVariants({ variant: "primary" }))}
									disabled={isFetchingNextPage}
									whileHover={{ y: -2 }}
									whileTap={{ y: 1 }}
								>
									{isFetchingNextPage ? (
										<div className="flex items-center gap-2">
											<Icon name="Loader" className="size-4 animate-spin" />
											{t("common.loading")}
										</div>
									) : (
										<div className="flex items-center gap-2">
											<Icon name="ArrowDown" className="size-4" />
											{t("home.browse.load_more")}
										</div>
									)}
								</motion.button>
							</motion.div>
						)}
					</>
				) : (
					<div className="col-span-full text-center text-muted-foreground pb-24">
						{isSearchMode && !submittedSearchQuery.trim() ? (
							<motion.div
								initial={{ opacity: 0, y: 10 }}
								animate={{ opacity: 1, y: 0 }}
							>
								<Icon
									name="Search"
									className="mx-auto h-12 w-12 mb-4 text-muted-foreground/50"
								/>
								<p>{t("home.browse.search_instruction")}</p>
								<p className="text-sm mt-2">
									{t("home.browse.search_instruction_submit")}
								</p>
							</motion.div>
						) : (
							<motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
								{t("home.browse.no_items")}
							</motion.p>
						)}
					</div>
				)}
			</motion.div>
		</section>
	);
}
